import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { Account, DomainDataConfig } from '../account/account.model';
import { AccountService } from 'app/account/account.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { IdentityService } from 'app/core/identity/identity.service';
import { CustomersService } from 'app/customers/customers.service';

@Component({
  selector: 'app-configure-market-account-modal',
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    TranslateModule,
  ],
  templateUrl: './configure-market-account-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './configure-market-account-modal.component.scss',
})
export class ConfigureMarketAccountModalComponent {
  private logger: Logger;
  public DomainDataConfig = DomainDataConfig;
  private account: Account;
  private createNewAccount: boolean;
  public dataDomain = {
    topDomain: {
      prefix: '',
      name: '',
    },
    subDomain: {
      prefix: '',
      name: '',
    },
    selectedOption: '',
    result: '',
    errors: null,
  };
  constructor(
    readonly loggerFactory: LoggerFactory,
    @Inject(MAT_DIALOG_DATA) public data: { account: Account; createNewAccount?: boolean },
    public dialogRef: MatDialogRef<ConfigureMarketAccountModalComponent>,
    private accountService: AccountService,
    private snackBarService: SnackBarService,
    private currentContextService: CurrentContextService,
    private readonly identityService: IdentityService,
    private readonly customersService: CustomersService,
  ) {
    this.logger = loggerFactory.get('ConfigureMarketAccountModalComponent');
    if (data) {
      this.initializeData(data);
    }
  }

  public initializeData(data: { account: Account; createNewAccount?: boolean }): void {
    const indexMarket = data.account.name.indexOf(DomainDataConfig.MarketAccount);

    let domain: string;
    if (data.createNewAccount) {
      const urls = data.account.urls;
      if (!urls || urls.length < 1) {
        throw Error('Cannot create new account when current does not have URL');
      }
      domain = data.account.urls[0];
    } else {
      domain = data.account.name;
    }
    const subDirectoryPrefix = indexMarket !== -1 ? domain.substring(0, indexMarket).trim() : domain;
    this.dataDomain.subDomain.prefix = subDirectoryPrefix.trim() + '/';
    const indexDot = subDirectoryPrefix.indexOf('.');
    const topLevelDomainPrefix = indexDot !== -1 ? subDirectoryPrefix.substring(0, indexDot + 1) : subDirectoryPrefix;
    this.dataDomain.topDomain.prefix = topLevelDomainPrefix.trim();
    this.dataDomain.selectedOption = DomainDataConfig.TopDomain;
    this.account = data.account;
    this.createNewAccount = data.createNewAccount;
  }

  public saveChanges(): void {
    if (!this.validateDomain()) return;
    this.dataDomain.result =
      this.dataDomain[this.dataDomain.selectedOption].prefix +
      this.dataDomain[this.dataDomain.selectedOption].name.trim();
    if (this.dataDomain.result) {
      if (this.createNewAccount) {
        const domain = this.dataDomain.selectedOption === 'subDomain' ? this.account.urls[0] : this.dataDomain.result;
        const customer = this.currentContextService.currentContext.customer;
        this.customersService
          .createSelfServiceAccount(customer.id, { domain: domain, name: this.dataDomain.result })
          .subscribe(async (response) => {
            this.logger.debug('Created accocunt', response);
            await this.identityService.requestUpdateIdentityPayloadAsync();

            customer.siteIds.push(response.siteId);
            this.currentContextService.updateCurrentContextCustomer(customer);
            this.snackBarService.success('generic.success');
            this.dialogRef.close();
          });
      } else {
        // Backward compatiblity for those having "Market accounts" created already
        const properties = [];
        if (this.dataDomain.selectedOption === DomainDataConfig.TopDomain) {
          properties.push({ propertyName: 'name', value: this.dataDomain.result });
          properties.push({ propertyName: 'urls', value: [this.dataDomain.result, ...this.account.urls.slice(1)] });
        } else if (this.dataDomain.selectedOption === 'subDomain') {
          properties.push({ propertyName: 'name', value: this.dataDomain.result });
        }
        this.accountService.patchAccountSettingsProperty(this.account.siteId, properties).subscribe(
          (data) => {
            this.currentContextService.loadCurrentContext(true);
            this.dialogRef.close();
            this.snackBarService.success('generic.success');
          },
          (err) => {
            this.dialogRef.close();
            this.snackBarService.error('generic.error');
          },
        );
      }
    }
  }

  public validateDomain(): boolean {
    this.dataDomain.errors = {};
    if (this.dataDomain.topDomain.name.trim().startsWith('.')) {
      this.dataDomain.errors.topDomain = 'generic.validation.error-domain';
      return false;
    }
    if (this.dataDomain.subDomain.name.trim().startsWith('/')) {
      this.dataDomain.errors.subDomain = 'generic.validation.error-domain';
      return false;
    }
    if (this.dataDomain.selectedOption === DomainDataConfig.SubDomain && !this.dataDomain.subDomain.name.trim()) {
      this.dataDomain.errors.subDomain = 'generic.validation.error-required';
      return false;
    }
    if (this.dataDomain.selectedOption === DomainDataConfig.TopDomain && !this.dataDomain.topDomain.name.trim()) {
      this.dataDomain.errors.topDomain = 'generic.validation.error-required';
      return false;
    }
    this.dataDomain.errors = null;
    return true;
  }

  public selectConfigDomain(): void {
    this.dataDomain.topDomain.name = '';
    this.dataDomain.subDomain.name = '';
    this.dataDomain.errors = null;
  }

  get isDataDomainEmpty(): boolean {
    return (
      (!this.dataDomain.topDomain.name.trim() && this.dataDomain.selectedOption === DomainDataConfig.TopDomain) ||
      (!this.dataDomain.subDomain.name.trim() && this.dataDomain.selectedOption === DomainDataConfig.SubDomain)
    );
  }
}
